

/**
 * States that mean this listing is NOT currently buyable.
 *
 * `pending_cancel` is included and it is the subtle one: the listing may well
 * come back to `success` if the cancel job fails. Treating it as not-live for
 * the window in which it is being withdrawn is the safe direction — the cost of
 * being wrong is a price hidden for a few seconds, where the inverse is
 * offering a sale that is being cancelled.
 */
const NOT_LIVE: readonly string[] = [
  "pending",
  "pending_approve",
  "pending_signature",
  "fail",
  "pending_cancel",
  "cancelled",
];

/**
 * The listing relation as it actually arrives.
 *
 * `[]` IS A REAL VALUE HERE, not a defensive guess: the API sends an empty
 * ARRAY for "this NFT has no listing" (verified live on a real token), the same
 * way it sends `meta: []` for an empty meta object elsewhere in this codebase.
 * `!!listing` is `true` for `[]`, so anything reading the relation has to rule
 * that out explicitly or it will go looking for a price on an array.
 */
export type ListingRelation =
  | ListingFields
  | unknown[]
  | null
  | undefined;

/**
 * Is this NFT for sale right now?
 *
 * Three things have to hold: a listing object (not the empty array), a real
 * positive price, and a status that is either absent or live.
 */
export function isListingLive(listing: ListingRelation): boolean {
  if (!listing || Array.isArray(listing)) return false;

  const price = Number((listing as { price_per_item?: unknown }).price_per_item);
  if (!Number.isFinite(price) || price <= 0) return false;

  const raw = (listing as { status?: unknown }).status;
  // Absent status = the relation is only surfacing live listings, which is the
  // documented contract and the shape production sends today.
  if (raw === undefined || raw === null || raw === "") return true;

  const status = String(raw).toLowerCase();
  if (NOT_LIVE.includes(status)) return false;
  // `success`, and anything this client has not met yet. Refusing an unknown
  // status would hide a live listing on the day a new one appears; the states
  // that genuinely mean "not for sale" are the ones enumerated above.
  return true;
}

/** True while a listing is mid-flight and its owner is the only one who can act. */
export function isListingInProgress(listing: ListingRelation): boolean {
  if (!listing || Array.isArray(listing)) return false;
  const raw = (listing as { status?: unknown }).status;
  if (raw === undefined || raw === null || raw === "") return false;
  const status = String(raw).toLowerCase();
  return status.startsWith("pending");
}

/** The object form of the relation, or null when there is no listing. */
export type ListingFields = {
  price_per_item?: number | string | null;
  quantity?: number | string | null;
  status?: string | null;
  chain_id?: number | null;
  currency_token_address?: string | null;
  /** The listed token, supplied directly by the NFT API. Its network is a
   * separate relation and must not be mistaken for the price unit. */
  currency?: {
    id?: number | null;
    name?: string | null;
    symbol?: string | null;
    image?: string | null;
    contract_address?: string | null;
    network?: { id?: number | null; name?: string | null; chain_id?: number | null } | null;
  } | null;
};

/**
 * Narrow the relation to something with fields on it.
 *
 * EXISTS BECAUSE `[]` IS A VALID VALUE. Every reader that wants a price, a
 * quantity or a currency has to rule the empty array out first, and doing that
 * at each call site is how one of them ends up not doing it. Returns null for
 * the array, for null and for undefined — the three ways this API says "none".
 */
export function readListing(listing: ListingRelation): ListingFields | null {
  if (!listing || Array.isArray(listing)) return null;
  return listing as ListingFields;
}
